/**
 * Hôte unique des dialogues, monté une fois dans l'arbre.
 * Affiche une demande à la fois ; les suivantes attendent leur tour.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { registerDialogHost } from './service.js';
import { BTN_GHOST, BTN_PRIMARY, EYEBROW, INPUT } from '../ui/theme.js';

export function DialogHost() {
  const { t } = useTranslation();
  const [queue, setQueue] = useState([]);
  const current = queue[0] ?? null;
  const [value, setValue] = useState('');
  const boxRef = useRef(null);
  const triggerRef = useRef(null);

  // Élément focalisé au moment de la demande (avant que l'autofocus du dialogue ne le déplace) :
  // le focus lui est rendu à la fermeture, plutôt que de retomber sur <body>.
  const hasCurrent = current !== null;
  useEffect(() => {
    if (hasCurrent) return undefined;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    if (trigger && trigger.isConnected && typeof trigger.focus === 'function') trigger.focus();
    return undefined;
  }, [hasCurrent]);

  useEffect(
    () =>
      registerDialogHost((request) => {
        if (!triggerRef.current) triggerRef.current = document.activeElement;
        setQueue((q) => [...q, request]);
      }),
    [],
  );

  // `key` remontée à chaque demande : la saisie du prompt précédent ne doit pas fuiter.
  useEffect(() => {
    setValue(current?.defaultValue ?? '');
  }, [current?.key, current?.defaultValue]);

  const close = useCallback(
    (result) => {
      current?.resolve(result);
      setQueue((q) => q.slice(1));
    },
    [current],
  );

  useEffect(() => {
    if (!current) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Capture + stopPropagation : Échap ne doit pas fermer aussi le panneau derrière.
      event.stopPropagation();
      event.preventDefault();
      close(current.type === 'confirm' ? false : current.type === 'prompt' ? null : undefined);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [current, close]);

  if (!current) return null;

  const { type, message, title, confirmLabel, cancelLabel } = current;
  const cancelValue = type === 'confirm' ? false : null;
  const titleId = `dlg-title-${current.key}`;
  const bodyId = `dlg-body-${current.key}`;

  return (
    <div
      data-dialog-service
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close(type === 'alert' ? undefined : cancelValue);
      }}
    >
      <div
        ref={boxRef}
        role={type === 'alert' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="w-[440px] max-w-full bg-paper p-3 shadow-plank"
      >
        <div className="relative border-2 border-line p-4">
          <span className="ticks" aria-hidden="true" />
          <p id={titleId} className={EYEBROW}>
            {title ||
              (type === 'confirm'
                ? t('dialogs.confirmTitle')
                : type === 'prompt'
                  ? t('dialogs.promptTitle')
                  : t('dialogs.infoTitle'))}
          </p>
          <p id={bodyId} className="mt-3 text-sm text-ink">
            {message}
          </p>

          {type === 'prompt' && (
            <input
              autoFocus
              className={`${INPUT} mt-3 w-full`}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') close(value);
              }}
            />
          )}

          <div className="mt-5 flex justify-end gap-2">
            {type !== 'alert' && (
              <button type="button" className={BTN_GHOST} onClick={() => close(cancelValue)}>
                {cancelLabel || t('dialogs.cancel')}
              </button>
            )}
            <button
              type="button"
              autoFocus={type !== 'prompt'}
              className={BTN_PRIMARY}
              onClick={() =>
                close(type === 'confirm' ? true : type === 'prompt' ? value : undefined)
              }
            >
              {confirmLabel || t('dialogs.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
